import {IEndPoint, IO} from "/@lib@/commons/io/io.js";
import {CoreUniverse} from "/@lib@/core/universe.js";
import {LOCALE} from "/@lib@/commons/lang.js";
import {JWspType, JWspTypeInst} from "/@lib@/wsp/wsp.js";

/** **/

export interface OPacksInUniverseConfig {
	/** Si la propriété est présente ET null, celà indique que le packMgr ne doit pas être instancié (ie fonction non active dans ce contexte) **/
	packs?: OPackSrvConfig
}

export interface IPacksInUniverse {
	packServer?: PackSrv;
}

/**
 * Serveur de packs : wspPack et skinPack
 */
export class PackSrv {
	constructor(public readonly universe: CoreUniverse, public readonly config: OPackSrvConfig) {}
}

export interface OPackSrvConfig {
	adminPackUrl: IEndPoint;
	skinPackUrl: IEndPoint;
}

export function configPackSrv(webFrameUrl: IEndPoint, config?: OPackSrvConfig): OPackSrvConfig {
	if (!config) config = {} as OPackSrvConfig;
	if (!config.adminPackUrl) config.adminPackUrl = webFrameUrl.resolve("u/adminPack/");
	if (!config.skinPackUrl) config.skinPackUrl = webFrameUrl.resolve("u/skinPack/");
	return config;
}

export interface JPackBase {
	id: string,
	title?: string,
	buildId?: string,
	installStatus?: any
}

// ###
// ### WSPPACKS
// ###

/**
 * Statut d'installation d'un wsp pack
 **/
export type EWspPackStatus = 'uploadPending' | 'installPending' | 'installed' | 'installFailed';
export type EWspPackErrors = 'unknownError' | 'versionPackUnknown' | 'versionFrameworkPackOutdated' | 'versionServerOutdated' | 'malformedPack';

/**
 * Props d'un pack en cours d'installation
 * @see eu.scenari.wsp.pack.impl.Pack.writeJson
 **/
export interface JWspPackBase extends JPackBase {
	installStatus: EWspPackStatus,
	/* Propriétés présente uniquement en cas d'erreur */
	error?: EWspPackErrors,
}

/**
 * Pack installé
 */
export interface JWspPackInstalled extends JWspPackBase {
	installStatus: 'installed',
	system?: boolean,
	installDate?: number,
	title: string,
	wspTypeDef: (JWspTypeDef | JWspOptionDef)[]
	framework: {
		version?: string,//FIXME : modéliser scVersion si besoin de comparaison
		minServerVersion?: string,//FIXME : modéliser scVersion si besoin de comparaison
	}
}


export namespace WSPPACK {

	/**
	 * Retourne true si la définition du modèle est de type "Option"
	 * @param def
	 */
	export function isOption(def: JWspTypeDef | JWspOptionDef): boolean {
		return (("isOption" in def) && (def as JWspOptionDef).isOption) ? true : false;
	}

	/**
	 * Retourne une forme lisible d'un JWspTypeDef ou JWspOptionDef
	 * @param def
	 */
	export function buildWspDefTitle(def: JWspTypeDef | JWspOptionDef | JWspType, options: { lang?: boolean, version?: boolean, short?: boolean } = {lang: true, version: true}): string {
		if (def) {
			let str = def.title || def.key;
			let opts = [];
			let lang = def.lang;
			if (options.lang && lang && lang.length > 0) opts.push(options.short ? lang : LOCALE.languageToName(lang));
			let version = def.version;
			if (options.version && version) opts.push(`version ${version}`);
			return str + (opts.length > 0 ? (" (" + opts.join(", ") + ")") : "");
		}
	}

	/**
	 * Retourne une forme lisible d'un JWspTypeInst
	 * @param def
	 */
	export function buildWspTypeTitle(def: JWspTypeInst, options: { ext: boolean | null, lang: boolean, version: boolean } = {ext: true, lang: true, version: true}): string {
		let finalTitle = [];
		finalTitle.push(WSPPACK.buildWspDefTitle(def.wspType, options));
		let wspOptionList = def.wspOptions || [];
		let count = wspOptionList.length;
		if (options.ext !== false && count > 0) {
			if (options.ext === null) {
				finalTitle.push(count > 1 ? `et ${count} extensions` : "et 1 extension");
			} else {
				wspOptionList.forEach((wspOption) => {
					finalTitle.push(WSPPACK.buildWspDefTitle(wspOption, options));
				});
			}
		}
		if (finalTitle.length > 0)
			return finalTitle.join(", ");
		return "Modèle documentaire";
	}


	/**
	 * Retourne une forme lisible du pack
	 * @param def
	 */
	export function buildPackTitle(pack: JWspPackInstalled | JWspPackBase): string {
		if (pack.title)
			return pack.title;
		let finalTitle = [];
		if ("wspTypeDef" in pack) {
			// - # Cas 1 : présence de wspTypeDef : on les affiche en priorité
			let wspTypeDefList = pack.wspTypeDef.filter((wspType) => {
				return ("isOption" in wspType) ? false : true;
			});
			let wspOptionDefList = pack.wspTypeDef.filter((wspType) => {
				return ("isOption" in wspType) ? true : false;
			});
			if (wspTypeDefList.length > 0) {
				wspTypeDefList.sort((wspTypeDefA: JWspTypeDef, wspTypeDefB: JWspTypeDef) => wspTypeDefA.title.localeCompare(wspTypeDefB.title));
				wspTypeDefList.forEach((wspTypeDef: JWspTypeDef) => finalTitle.push(WSPPACK.buildWspDefTitle(wspTypeDef)));
				let count = wspOptionDefList.length;
				if (wspOptionDefList.length > 0) finalTitle.push(count > 1 ? `et ${count} extensions` : "et 1 extension");
			} else {
				// - # Cas 2 : pas de wspTypeDef : on affiche la liste des extensions
				wspOptionDefList.sort((wspTypeDefA: JWspOptionDef, wspTypeDefB: JWspOptionDef) => wspTypeDefA.title.localeCompare(wspTypeDefB.title));
				wspOptionDefList.forEach((wspTypeDef: JWspOptionDef) => finalTitle.push(WSPPACK.buildWspDefTitle(wspTypeDef)));
			}
		}
		if (finalTitle.length > 0)
			return finalTitle.join(", ");
		return "Modèle documentaire";
	}

	/**
	 * Retourne l'erreur sous une forme présentable à l'utilisateur
	 * @param packError
	 */
	export function errorToString(packError: EWspPackErrors): string {
		if (packError == null) return;
		let errorsStr = "";
		switch (packError) {
		case "versionFrameworkPackOutdated":
		case "versionPackUnknown":
			errorsStr = "Pack obsolète, mise à jour nécessaire";
			break;
		case "versionServerOutdated":
			errorsStr = "Pack trop récent incompatible avec cet entrepôt";
			break;
		case "malformedPack":
		case "unknownError":
		default :
			errorsStr = "Pack en erreur";
		}
		return errorsStr;
	}

	/**
	 * Retourne true si le statut d'installation du pack n'est pas stable
	 * @param status
	 */
	export function isStatusPending(status: EWspPackStatus): boolean {
		return (status === "uploadPending" || status === "installPending");
	}

	/**
	 * Retourne true si le pack est installé
	 * @param status
	 */
	export function isStatusInstalled(status: EWspPackStatus): boolean {
		return status === "installed";
	}

	/**
	 * Retourne la liste des packs installés ou en cours
	 * @see eu.scenari.wsp.service.adminpack.SvcAdminPackDialog.CDACTION_ListPacks
	 * @param packServer
	 * @constructor
	 */
	export async function listPacks(packServer: PackSrv): Promise<(JWspPackInstalled | JWspPackBase)[]> {
		return packServer.config.adminPackUrl.fetchJson<(JWspPackInstalled | JWspPackBase)[]>(IO.qs("cdaction", "ListPacks"));
	}

	/**
	 * Retourne la liste des wspTypeDef/wspOptionDef disponibles dans le gestionnaire de packs
	 * @see eu.scenari.wsp.service.adminpack.SvcAdminPackDialog.CDACTION_ListWspTypesDef
	 * @param packServer
	 * @constructor
	 */
	export async function listWspTypesDef(packServer: PackSrv, skinClasses?: string[], pIncludeOptions = false): Promise<(JWspTypeDef | JWspOptionDef)[]> {
		return packServer.config.adminPackUrl.fetchJson<(JWspTypeDef | JWspOptionDef)[]>(IO.qs("cdaction", "ListWspTypesDef", "skinClasses", skinClasses ? skinClasses.join(",") : null, "includeOptions", pIncludeOptions));
	}

	/**
	 * Retourne les informations relatives à un pack
	 * @see eu.scenari.wsp.service.adminpack.SvcAdminPackDialog.CDACTION_InfoPack
	 * @param packServer
	 * @param packId : id du pack
	 * @constructor
	 */
	export async function infoPack(packServer: PackSrv, packId: string): Promise<JWspPackInstalled | JWspPackBase> {
		let resp = await packServer.config.adminPackUrl.fetchJson<any>(IO.qs("cdaction", "InfoPack", "param", packId)) as { pack: JWspPackInstalled | JWspPackBase };
		return resp.pack;
	}

	/**
	 * Installe un pack
	 * @see eu.scenari.wsp.service.adminpack.SvcAdminPackDialog.CDACTION_InstallPack
	 * @param packServer
	 * @param content
	 * @param sync : installation synchrone (si true, l'objet pack est retourné à l'issue de l'istallation)
	 * @constructor
	 */
	export async function installPack(packServer: PackSrv, content: File, sync: boolean = false): Promise<JWspPackInstalled | JWspPackBase> {
		//TODO UI progression upload + cancelable
		let resp = await packServer.config.adminPackUrl.fetchJson<any>(IO.qs("cdaction", "InstallPack", "sync", sync), {
			method: "PUT",
			body: content
		}) as { pack: JWspPackInstalled | JWspPackBase };
		return resp.pack;
	}

	/**
	 * Désinstalle un pack
	 * @param packServer
	 * @param packId : id du pack
	 */
	export async function uninstallPack(packServer: PackSrv, packId: string): Promise<void | null> {
		return packServer.config.adminPackUrl.fetchVoid(IO.qs("cdaction", "UninstallPack", "param", packId), {method: "POST"});
	}

	/**
	 * Télécharge les sources d'un pack
	 * @param packServer
	 * @param packId : id du pack
	 */
	export async function downloadPack(packServer: PackSrv, packId: string): Promise<Blob> {
		return packServer.config.adminPackUrl.fetchBlob(IO.qs("cdaction", "DownloadPack", "param", packId));
	}

	export function getDownloadPackUrl(packServer: PackSrv, packId: string): string {
		return packServer.config.adminPackUrl.resolve(IO.qs("cdaction", "DownloadPack", "param", packId)).url;
	}

}


// ###
// ### SKINS
// ###
export type ESkinPackStatus = 'uploadPending' | 'installed' | 'installFailed';

export type ESkinPackErrors = 'unknownError' | 'malformedPack';


export interface JSkinPackInstalled extends JPackBase {
	buildId?: string,
	skinSetCode: string,
	skinSetIcon?: string,
	/* Propriétés présente uniquement en cas d'erreur */
	error?: ESkinPackErrors,
	/** skinClasses : ATTENTION, la liste active pour un modèle donné peut être un *sous-ensemble** de cette liste globale */
	skinClasses?: string[],
	installStatus: ESkinPackStatus,
	version: number,
	installDate: number,
}


export namespace SKINPACK {

	/**
	 * Retourne une forme lisible du pack
	 * @param def
	 */
	export function buildPackTitle(pack: JSkinPackInstalled | JWspPackBase): string {
		const title = pack.title || "Habillage graphique";
		const version = (pack as JSkinPackInstalled).version;
		return version ? `${title} (version ${version})` : title;
	}

	/**
	 * Retourne l'erreur sous une forme présentable à l'utilisateur
	 * @param packError
	 */
	export function errorToString(packError: ESkinPackErrors): string {
		if (packError == null) return;
		let errorsStr = "";
		switch (packError) {
		case "malformedPack":
			errorsStr = "Habillage graphique incompatible avec cet environnement";
			break;
		case "unknownError":
		default :
			errorsStr = "Habillage graphique en erreur";
		}
		return errorsStr;
	}

	/**
	 * Retourne true si le statut d'installation du pack n'est pas stable
	 * @param status
	 */
	export function isStatusPending(status: ESkinPackStatus): boolean {
		return (status === "uploadPending");
	}

	/**
	 * Retourne true si le pack est installé
	 * @param status
	 */
	export function isStatusInstalled(status: ESkinPackStatus): boolean {
		return status == "installed" ? true : false;
	}

	/**
	 * Retourne la liste des skinpacks installés
	 * @see eu.scenari.wsp.service.skinpack.SvcSkinPackDialog.CDACTION_List
	 * @param packServer
	 * @constructor
	 */
	export async function list(packServer: PackSrv): Promise<(JSkinPackInstalled)[]> {
		return packServer.config.skinPackUrl.fetchJson<(JSkinPackInstalled)[]>(IO.qs("cdaction", "List"));
	}

	/**
	 * Installe un skinpack
	 * @see eu.scenari.wsp.service.skinpack.SvcSkinPackDialog.CDACTION_Install
	 * @param packServer
	 * @param content
	 * @constructor
	 */
	export async function install(packServer: PackSrv, content: File): Promise<JSkinPackInstalled> {
		//TODO UI progression upload + cancelable
		let resp = await packServer.config.skinPackUrl.fetchJson<any>(IO.qs("cdaction", "Install"), {
			method: "PUT",
			body: content
		}) as { pack: JSkinPackInstalled };
		return resp.pack;
	}

	/**
	 * Désinstalle un skinpack
	 * @see eu.scenari.wsp.service.skinpack.SvcSkinPackDialog.CDACTION_Delete
	 * @param packServer
	 * @param skinPackId : id du skinpack
	 */
	export async function deleteSkin(packServer: PackSrv, skinPackId: string): Promise<void | null> {
		return packServer.config.skinPackUrl.fetchVoid(IO.qs("cdaction", "Delete", "skinPackId", skinPackId), {method: "POST"});
	}

	/**
	 * Télécharge les sources d'un skinpack
	 * @see eu.scenari.wsp.service.skinpack.SvcSkinPackDialog.CDACTION_Download
	 * @param packServer
	 * @param skinPackId : id du skinpack
	 */
	export async function downloadPack(packServer: PackSrv, skinPackId: string): Promise<Blob> {
		return packServer.config.skinPackUrl.fetchBlob(IO.qs("cdaction", "Download", "skinPackId", skinPackId));
	}

	export function getDownloadPackUrl(packServer: PackSrv, skinPackId: string): string {
		return packServer.config.skinPackUrl.resolve(IO.qs("cdaction", "Download", "skinPackId", skinPackId)).url;
	}

	/**
	 * Retourne l'URL de l'icône du skin
	 * @see eu.scenari.wsp.service.skinpack.SvcSkinPackDialog.CDACTION_GetIcon
	 * @param packServer
	 * @param skinPackCode : code du skinpack (ATTENTION : pas l'id)
	 */
	export function getIconUrl(packServer: PackSrv, skinPackCode: string): string {
		return packServer.config.skinPackUrl.resolve(IO.qs("cdaction", "GetIcon", "param", skinPackCode)).url;
	}

	/**
	 * Liste tous les skinClass d'un skinSet donné actifs pour tous les wspTypes installés / ou le wspType spécifié
	 * @see eu.scenari.wsp.service.skinpack.SvcSkinPackDialog.CDACTION_ListSkinClassesForWspType
	 * @param skinPackId
	 * @param keyWspType : key du wspType
	 * @param langWspType : langue du wspType
	 * @param versionWspType : version du wspType
	 */
	export function ListSkinClassesForWspType(packServer: PackSrv, skinPackId: string, keyWspDef?: string, versionWspDef?: string, langWspDef?: string): Promise<JSkinClassesForWspType[]> {
		return packServer.config.skinPackUrl.fetchJson(IO.qs("cdaction", "ListSkinClassesForWspType", "param", skinPackId, "wspTypeKey", keyWspDef, "version", versionWspDef, "lang", langWspDef));
	}

	export interface JSkinClassesForWspType {
		wspTypeRef: JWspTypeDef | JWspOptionDef,
		skinsClasses: string[],
	}

}

// ###
// ### Wsp*Def
// ###

/**
 * WspTypeDef
 * @see eu.scenari.wsp.pack.impl.WspTypeDef.writeJson
 */
export interface JWspTypeDef {
	key: string,
	lang: string | null,
	uri: string,
	title: string,
	version: string,//FIXME : modéliser scVersion si besoin de comparaison
	skinClasses?: JSkinClassDef[]
	generators?: JGeneratorDef[]
}

export interface JSkinClassDef {
	class: string,
	name?: string,
	sgn?: string,
	skins?: JSkinEntry[]
}

export interface JSkinEntry {
	code: string,
	name?: string,
	keyRes?: string,
	path?: string,
	iconPath?: string,
}

export interface JGeneratorDef {
	code: string,
	name?: string,
	sgn?: string,
	skinClass?: string,
	rootModels?: string[],
}

/**
 * WspOptionDef
 * @see  eu.scenari.wsp.pack.impl.WspOptionDef.writeJson
 */
export interface JWspOptionDef extends JWspTypeDef {
	isOption: true,
	nature: "skin" | "gen" | "ext",
	parentKey: string,
	parentMinVersion?: string,//FIXME : modéliser scVersion si besoin de comparaison
	parentMaxVersion?: string,//FIXME : modéliser scVersion si besoin de comparaison
	parentLang?: string,
}